/**
 * What the signed-in user may do, as the workspace reads it.
 *
 * **This is presentation, never enforcement.** Every gate in the interface is already enforced by
 * the API: a hidden button is not security, a route guard is not an authorisation decision, and a
 * 403 is a state to display rather than a bug (authorisation in the UI). What this file buys is
 * that an agent is not shown a supervisor's board and then refused it — which is a courtesy, not a
 * boundary.
 *
 * The rules mirror `packages/contexts/tenant-identity/src/domain/scope.ts`, which is the one that
 * decides. They are restated here rather than imported because that package is a NestJS context an
 * app may only reach through its index, and because the browser must not carry the server's domain
 * to answer a question about whether to draw a link.
 */

export const scopeTypes = ['tenant', 'team', 'queue'] as const;
export type ScopeType = (typeof scopeTypes)[number];

export interface ScopedPermission {
  readonly permission: string;
  readonly scopeType: ScopeType;
  /** The teams or queues it covers. Empty for `tenant`, which covers everything. */
  readonly scopeIds: readonly string[];
}

/** Who is asking, as `/v1/me` answers it. */
export interface Principal {
  readonly userId: string;
  readonly permissions: readonly ScopedPermission[];
}

/** What is being asked about. An absent field is "not scoped by that", not "any". */
export interface Resource {
  readonly queueId?: string;
  readonly teamId?: string;
  /** The user the resource belongs to, for the ownership rules scoping cannot express. */
  readonly ownerId?: string;
}

/**
 * Whether the principal may do this, to this — the same answer the API computes.
 *
 * A resource that names nothing the permission is scoped by is refused rather than allowed, as it
 * is server-side: a screen that forgot to say which queue it was about must not render as though
 * the whole tenant were in scope.
 */
export function allowed(
  principal: Principal,
  permission: string,
  resource: Resource = {},
): boolean {
  const held = principal.permissions.filter((scoped) => scoped.permission === permission);
  if (held.length === 0) {
    return false;
  }
  if (resource.ownerId !== undefined && resource.ownerId === principal.userId) {
    return true;
  }
  return held.some((scoped) => {
    if (scoped.scopeType === 'tenant') {
      return true;
    }
    const named = scoped.scopeType === 'queue' ? resource.queueId : resource.teamId;
    return named !== undefined && scoped.scopeIds.includes(named);
  });
}

/**
 * Whether the principal holds a permission **anywhere**.
 *
 * This is the question navigation asks, and it is deliberately a different question from `allowed`:
 * a supervisor of one team should see the supervision area, and `allowed` would refuse them because
 * a persona link names no team. The screen behind the link then asks `allowed` per queue or team,
 * and the API refuses anything either of them got wrong.
 */
export function holdsPermission(principal: Principal, permission: string): boolean {
  return principal.permissions.some((scoped) => scoped.permission === permission);
}

/**
 * Every queue or team a principal holds a permission over, or `all` for a tenant-wide assignment.
 *
 * What a board filters its own request by, so it asks for what it may see instead of asking for
 * everything and showing whatever came back.
 */
export function scopeFor(
  principal: Principal,
  permission: string,
): { readonly all: true } | { readonly all: false; readonly ids: readonly string[] } {
  const held = principal.permissions.filter((scoped) => scoped.permission === permission);
  if (held.some((scoped) => scoped.scopeType === 'tenant')) {
    return { all: true };
  }
  const ids = [...new Set(held.flatMap((scoped) => scoped.scopeIds))];
  return { all: false, ids };
}
